import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { useTags, useCreateTag } from '@/hooks/tags/useTags';
import { NOTIFI_PASTEL_COLORS } from '@/lib/design/brand-colors';
import { cn } from '@/lib/utils';

// A small preset palette for new tags (the create API accepts an optional color).
const PRESET_COLORS = NOTIFI_PASTEL_COLORS;

type DraftTag = {
  id: string;
  name: string;
  color: string;
};

/**
 * Per-add tag dialog for the detail-page Tags card — mirrors EntityCustomFieldDialog:
 * a designed form (existing, not-yet-assigned tags + a "Create new tag" path with
 * Name + color) and Cancel/Add. Assignment persists through the caller's `onChange`.
 */
export function EntityTagDialog({
  scope,
  value,
  onChange,
  open,
  onOpenChange,
}: {
  scope: string;
  value: string[];
  onChange: (tagIds: string[]) => void;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { data: allTags = [] } = useTags(scope);
  const createTag = useCreateTag(scope);

  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PRESET_COLORS[0]!);
  const [draftTags, setDraftTags] = useState<DraftTag[]>([]);
  const [draftCounter, setDraftCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const baseAssignable = allTags.filter((t) => !value.includes(t.id));
  const selectedCount = selectedIds.length + selectedDraftIds.length;
  const hasSelectableTags = baseAssignable.length > 0 || draftTags.length > 0;

  // Reset the form each time the dialog opens; default to create mode when there are
  // no unassigned tags left to pick.
  useEffect(() => {
    if (!open) return;
    setMode(baseAssignable.length > 0 ? 'select' : 'create');
    setSelectedIds([]);
    setSelectedDraftIds([]);
    setName('');
    setColor(PRESET_COLORS[0]!);
    setDraftTags([]);
    setDraftCounter(0);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleSelected(tagId: string) {
    setSelectedIds((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId],
    );
  }

  function toggleDraftSelected(tagId: string) {
    setSelectedDraftIds((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId],
    );
  }

  async function add() {
    if (mode === 'create') {
      const trimmed = name.trim();
      if (!trimmed) return;
      // Don't create a duplicate: if a tag with this exact name already exists,
      // select it instead of queuing a second one / hitting an API validation error.
      const normalized = trimmed.toLowerCase();
      const existing = allTags.find((t) => t.name.toLowerCase() === normalized);
      const existingDraft = draftTags.find((t) => t.name.toLowerCase() === normalized);
      if (existing) {
        if (!value.includes(existing.id)) {
          setSelectedIds((current) =>
            current.includes(existing.id) ? current : [...current, existing.id],
          );
        }
      } else if (existingDraft) {
        setSelectedDraftIds((current) =>
          current.includes(existingDraft.id) ? current : [...current, existingDraft.id],
        );
      } else {
        const draftId = `draft-${draftCounter}`;
        setDraftCounter((current) => current + 1);
        setDraftTags((current) => [...current, { id: draftId, name: trimmed, color }]);
        setSelectedDraftIds((current) => [...current, draftId]);
      }
      setName('');
      setColor(PRESET_COLORS[0]!);
      setMode('select');
      return;
    } else {
      if (selectedCount === 0) return;

      setIsSaving(true);
      try {
        let nextValue = [...new Set([...value, ...selectedIds])];
        const selectedDrafts = draftTags.filter((tag) => selectedDraftIds.includes(tag.id));
        const applyNextValue = (tagIds: string[]) => {
          nextValue = [...new Set(tagIds)];
          onChange(nextValue);
        };

        if (selectedIds.length > 0) {
          applyNextValue(nextValue);
        }

        for (const draft of selectedDrafts) {
          const existing = allTags.find(
            (tag) => tag.name.toLowerCase() === draft.name.toLowerCase(),
          );
          let tagId = existing?.id;
          if (!tagId) {
            const created = await createTag.mutateAsync({
              name: draft.name,
              color: draft.color,
            });
            tagId = created.id;
          }

          if (!nextValue.includes(tagId)) {
            applyNextValue([...nextValue, tagId]);
          }

          setDraftTags((current) => current.filter((tag) => tag.id !== draft.id));
          setSelectedDraftIds((current) => current.filter((id) => id !== draft.id));
          setSelectedIds((current) => (current.includes(tagId) ? current : [...current, tagId]));
        }

        onOpenChange(false);
      } finally {
        setIsSaving(false);
      }
    }
  }

  const canAdd = mode === 'create' ? name.trim().length > 0 : selectedCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add tag</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {mode === 'select' ? (
            <>
              <div className="space-y-1.5">
                <Label>Tags</Label>
                <div className="max-h-56 overflow-y-auto rounded-md border">
                  {hasSelectableTags ? (
                    <>
                      {baseAssignable.map((t) => {
                        const selected = selectedIds.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            aria-pressed={selected}
                            className={cn(
                              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50',
                              selected && 'bg-muted',
                            )}
                            onClick={() => toggleSelected(t.id)}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{
                                backgroundColor: t.color ?? 'hsl(var(--muted-foreground))',
                              }}
                            />
                            <span className="min-w-0 flex-1 truncate">{t.name}</span>
                            {selected && <Check className="size-4 shrink-0" />}
                          </button>
                        );
                      })}
                      {draftTags.map((t) => {
                        const selected = selectedDraftIds.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            aria-pressed={selected}
                            className={cn(
                              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50',
                              selected && 'bg-muted',
                            )}
                            onClick={() => toggleDraftSelected(t.id)}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: t.color }}
                            />
                            <span className="min-w-0 flex-1 truncate">{t.name}</span>
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              New
                            </span>
                            {selected && <Check className="size-4 shrink-0" />}
                          </button>
                        );
                      })}
                    </>
                  ) : (
                    <p className="px-3 py-4 text-sm text-muted-foreground">
                      No tags left — create one.
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => setMode('create')}
              >
                + Create new tag
              </button>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="tag-name">Name</Label>
                <Input
                  id="tag-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tag name"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={`Color ${c}`}
                      onClick={() => setColor(c)}
                      className={cn(
                        'h-6 w-6 rounded-full border-2 transition-transform hover:scale-110',
                        color === c ? 'border-foreground' : 'border-transparent',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
              {hasSelectableTags && (
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={() => setMode('select')}
                >
                  ← Pick an existing tag
                </button>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={add} disabled={!canAdd || createTag.isPending || isSaving}>
            {mode === 'create'
              ? 'Add to selection'
              : isSaving
                ? 'Adding...'
                : selectedCount > 1
                  ? `Add ${selectedCount} tags`
                  : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
